import type { PropertyResult, QueryParams } from '../../../hooks/climateData'
import type { HazardInfo } from '../../../types/climate'
import { CanonicalPeriod, EmissionTier, MetricState, type AdaptationView } from '../common'
import { parseMetricPointY } from '../common/financialMetrics'
import { mapHeatIndexToCategory } from '../common/heatStress/utils'
import { normalizeWaterRiskScore, parseWaterStressScore } from '../common/waterRisk/utils'
import { getClimateHazardViewConfig } from '../common/hazardConfig'

export type RiskBand = 1 | 2 | 3 | 4 | 5
export type AdaptationProvenance = 'adapted' | 'baseline' | 'mixed' | 'unknown'

export const PORTFOLIO_OVERVIEW: HazardInfo = {
  id: -1,
  name: 'overview',
  display_name: 'Overview',
  description: 'Compare hazards across your portfolio and explore the risk profile of each property.',
}

const standardLabels: Record<RiskBand, string> = {
  1: 'Low',
  2: 'Moderate',
  3: 'Moderate to High',
  4: 'High',
  5: 'Very High',
}

export type HazardRiskConfig = {
  metricKey: string
  labels: Record<RiskBand, string>
  normalize: (raw: number | null) => RiskBand | null
  periodized: boolean
}

function normalizeRating(value: number | null): RiskBand | null {
  if (value == null || !Number.isFinite(value) || value < 1 || value > 5) return null
  return Math.round(value) as RiskBand
}

export const HAZARD_RISK_CONFIG: Record<string, HazardRiskConfig> = {
  fld: { metricKey: 'fld:score', labels: standardLabels, normalize: normalizeRating, periodized: true },
  wf: {
    metricKey: 'wf:score',
    labels: { ...standardLabels, 1: 'Negligible', 2: 'Low', 3: 'Moderate' },
    normalize: normalizeRating,
    periodized: true,
  },
  tc: {
    metricKey: 'tc:score',
    labels: { ...standardLabels, 1: 'Very Low', 2: 'Low', 3: 'Moderate' },
    normalize: normalizeRating,
    periodized: false,
  },
  wind_gust: {
    metricKey: 'wind_gust:score',
    labels: { ...standardLabels, 1: 'Very Low', 2: 'Low', 3: 'Moderate' },
    normalize: normalizeRating,
    periodized: true,
  },
  freeze_thaw: {
    metricKey: 'freeze_thaw:score',
    labels: standardLabels,
    normalize: normalizeRating,
    periodized: true,
  },
  hs: {
    metricKey: 'hs:heat_scores',
    labels: standardLabels,
    normalize: value => mapHeatIndexToCategory(value) as RiskBand | null,
    periodized: true,
  },
  wr: {
    metricKey: 'wr:ws',
    labels: { ...standardLabels, 2: 'Low-Medium', 3: 'Medium-High', 5: 'Extremely High' },
    normalize: value => normalizeWaterRiskScore(value) as RiskBand | null,
    periodized: true,
  },
}

export const RISK_BANDS: RiskBand[] = [1, 2, 3, 4, 5]

export type PropertyRisk = {
  score: RiskBand | null
  rawScore: number | null
  state: MetricState
  provenance: AdaptationProvenance
  scenario: string | null
}

export type HazardRiskSummary = {
  average: number | null
  highCount: number | null
  contributors: number
  total: number
  distribution: Record<RiskBand, number>
  adaptedContributors: number
  baselineContributors: number
  unknownContributors: number
}

export type PortfolioHazardRisk = {
  hazard: HazardInfo
  config: HazardRiskConfig | undefined
  periodAvailable: boolean
  adaptationsEnabled: boolean
  loading: boolean
  error: boolean
  byProperty: Record<number, PropertyRisk>
  summary: HazardRiskSummary
  retry?: () => void
}

export function hazardRiskQueryParams(
  hazardCode: string,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
  view: AdaptationView,
): QueryParams {
  const config = HAZARD_RISK_CONFIG[hazardCode]
  return {
    emissionTiers: [emissionTier],
    // Tropical cyclone is a single modelled period, not a canonical-period series.
    canonicalPeriods: config?.periodized ? [period] : null,
    view: getClimateHazardViewConfig(hazardCode).supportsAdaptations ? view : 'baseline',
    ...(hazardCode === 'wr' ? { facetQuery: { variable: 'score' } } : {}),
  }
}

export function extractPropertyRisk(
  hazardCode: string,
  entry: PropertyResult | undefined,
  emissionTier: EmissionTier,
  period: CanonicalPeriod,
  adaptationsEnabled: boolean,
): PropertyRisk {
  const config = HAZARD_RISK_CONFIG[hazardCode]
  const empty: PropertyRisk = {
    score: null,
    rawScore: null,
    state:
      entry?.state?.value === MetricState.SUCCEEDED ? MetricState.NO_DATA : entry?.state?.value ?? MetricState.NO_RUN,
    provenance: 'unknown',
    scenario: null,
  }
  if (!config || entry?.state?.value !== MetricState.SUCCEEDED || !entry.data) return empty
  const tierNode = entry.data.data?.[emissionTier]
  const rawScore =
    hazardCode === 'wr'
      ? parseWaterStressScore(entry, emissionTier, period)
      : parseMetricPointY(config.periodized ? tierNode?.[period] : tierNode)
  const score = config.normalize(rawScore)
  const metadata = entry.data.adaptation_by_tier?.[emissionTier]
  const provenance: AdaptationProvenance = !adaptationsEnabled
    ? 'baseline'
    : metadata === 'adapted' || metadata === 'baseline' || metadata === 'mixed'
    ? metadata
    : 'unknown'
  return {
    ...empty,
    score,
    rawScore,
    // A successful metric can still lack the selected scenario/horizon.
    state: score == null ? MetricState.NO_DATA : MetricState.SUCCEEDED,
    provenance,
    scenario: entry.data.resolved_scenarios_by_tier?.[emissionTier] ?? null,
  }
}

export function summarizeHazardRisk(
  propertyIds: number[],
  byProperty: Record<number, PropertyRisk>,
): HazardRiskSummary {
  const contributors = propertyIds.map(id => byProperty[id]).filter(value => value?.score != null)
  const distribution: Record<RiskBand, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  contributors.forEach(value => {
    distribution[value.score!] += 1
  })
  return {
    average: contributors.length
      ? contributors.reduce((sum, value) => sum + value.score!, 0) / contributors.length
      : null,
    highCount: contributors.length ? distribution[4] + distribution[5] : null,
    contributors: contributors.length,
    total: propertyIds.length,
    distribution,
    adaptedContributors: contributors.filter(value => value.provenance === 'adapted').length,
    baselineContributors: contributors.filter(value => value.provenance === 'baseline').length,
    unknownContributors: contributors.filter(value => value.provenance === 'unknown' || value.provenance === 'mixed')
      .length,
  }
}

export function supportedHorizonLabel(hazardCode: string): string {
  const config = getClimateHazardViewConfig(hazardCode)
  return config.availablePeriods.map(period => ({ short: '2030', mid: '2050', long: '2100' }[period])).join(', ')
}

export function isHazardPeriodAvailable(hazardCode: string, period: CanonicalPeriod): boolean {
  return getClimateHazardViewConfig(hazardCode).availablePeriods.includes(period)
}

export function parseFinancialValue(value: unknown): number | null {
  if (value == null || value === '') return null
  const numeric = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN
  return Number.isFinite(numeric) ? numeric : null
}

export type PortfolioRiskProperty = {
  id: number
  name?: string
  address?: { street?: string; town?: string; postcode?: string; country?: string } | null
  type?: { label?: string } | null
  insurance?: {
    structure?: number | string | null
    contents?: number | string | null
    revenue?: number | string | null
  } | null
}

export function insuredPropertyValue(property: PortfolioRiskProperty): number | null {
  const structure = parseFinancialValue(property.insurance?.structure)
  const contents = parseFinancialValue(property.insurance?.contents)
  return structure == null && contents == null ? null : (structure ?? 0) + (contents ?? 0)
}

export function summarizePortfolioExposure(properties: PortfolioRiskProperty[]) {
  const values = properties.map(insuredPropertyValue).filter((value): value is number => value != null)
  return {
    value: values.length ? values.reduce((sum, value) => sum + value, 0) : null,
    contributors: values.length,
    total: properties.length,
    structureContributors: properties.filter(property => parseFinancialValue(property.insurance?.structure) != null)
      .length,
    contentsContributors: properties.filter(property => parseFinancialValue(property.insurance?.contents) != null)
      .length,
  }
}
